import { useClient } from '@cortex/react-query';
import type { IPaginated, Run, WorkflowVersion } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, type CSSProperties } from 'react';

import { routes } from '../../../lib/constants';
import { formatDuration, formatRelative } from '../../../lib/format';
import { usePaginatedList } from '../../../lib/use-paginated-list';
import { EmptyState } from '../../common/EmptyState';
import { InfiniteScrollSentinel } from '../../common/InfiniteScrollSentinel';
import { Spinner } from '../../common/Spinner';
import { StatusBadge } from '../../common/StatusBadge';
import { useWorkflowEditor } from '../WorkflowEditorContext';

import { InlineRunDetail } from './InlineRunDetail';

const pageSize = 20;

const cardListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
  padding: 'var(--sp-3)',
};

const cardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
  padding: 'var(--sp-3)',
  background: 'var(--surface-2)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  transition: 'border-color 120ms ease',
};

const cardHeaderRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--sp-2)',
};

const cardMetaRow: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--sp-3)',
  fontSize: 'var(--fs-xs)',
  color: 'var(--text-secondary)',
};

/**
 * Right-panel view for runs. Two modes determined entirely by the URL:
 *
 * - `panel=runs` (no `runId`) — vertical card list of recent runs.
 *   Clicking a card opens it inline (`onOpenRun`).
 * - `panel=runs&runId=…` — live tail for that run, using the same
 *   `RunPanel` widget the old editor sidebar used. Falls back to the
 *   list when the URL doesn't pin a run.
 *
 * The `activeRunId` sync is done via `useEffect` (not during render) to
 * avoid React's "setState while rendering another component" warning.
 * Closing the tail clears both the URL `runId` AND the editor's
 * `activeRunId` so the shell's auto-pin effect doesn't snap the user
 * straight back to the run they just left.
 */
export function RunsPanel({
  runId,
  onOpenRun,
}: {
  runId: string | null;
  onOpenRun: (runId: string) => void;
}) {
  const client = useClient();
  const { workflow, activeRunId, setActiveRunId } = useWorkflowEditor();

  // Keep the editor's `activeRunId` aligned with whatever the URL pins. The
  // canvas reads `activeRunId` to paint per-node state during a live run;
  // updating it during render would queue a state update on the provider
  // mid-render of this child, which React warns about.
  useEffect(() => {
    if (runId && activeRunId !== runId) setActiveRunId(runId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  // Drop the canvas run-state when the user leaves the runs panel
  // entirely (e.g. switches to Overview / Schemas). This component only
  // unmounts on that navigation — switching between the run LIST and a
  // run DETAIL keeps it mounted — so an unmount-time clear deselects the
  // run without clobbering the run-start auto-pin (which mounts, never
  // unmounts, this panel). Without it the completed run keeps painting
  // its node/edge state on the canvas after you've navigated off.
  useEffect(() => {
    return () => setActiveRunId(null);
  }, [setActiveRunId]);

  // Infinite scroll: an IntersectionObserver sentinel below the cards
  // calls `loadMore` when the user scrolls near the end. The panel's own
  // scroll container is the `overflowY: auto` wrapper provided by
  // `right-panel/index.tsx`, so the main canvas stays fixed and only
  // the panel scrolls. (DEV-16164.)
  const list = usePaginatedList<Run>({
    enabled: runId === null,
    queryKey: ['runs', { workflowId: workflow.id, pageSize }],
    pageSize,
    fetchPage: ({ limit, offset }) =>
      client.http
        .get(`v1/runs?workflowId=${workflow.id}&limit=${limit}&offset=${offset}`)
        .json<IPaginated<Run>>(),
  });

  // Resolve each run's workflowVersionId → "v{n}" for display on the cards.
  const versions = useQuery({
    enabled: runId === null,
    queryKey: ['workflow-versions', workflow.id],
    queryFn: () =>
      client.http.get(`v1/workflows/${workflow.id}/versions`).json<WorkflowVersion[]>(),
  });
  const versionLabel = useMemo(() => {
    const m = new Map<string, number>();
    for (const v of versions.data ?? []) m.set(v.id, v.versionNumber);
    return (id: string): string => {
      const n = m.get(id);
      return n !== undefined ? `v${n}` : id.slice(0, 8);
    };
  }, [versions.data]);

  if (runId !== null) {
    return (
      <InlineRunDetail
        runId={runId}
        onClose={() => {
          // Clear the editor-side active run before the URL changes so
          // the shell's auto-pin effect doesn't immediately re-pin us.
          setActiveRunId(null);
          onOpenRun('');
        }}
      />
    );
  }

  if (list.isLoading) return <Spinner />;
  if (list.error) {
    return (
      <div className="alert alert--danger" style={{ margin: 'var(--sp-3)' }}>
        Failed to load runs: {list.error.message}
      </div>
    );
  }

  if (list.items.length === 0) {
    return (
      <EmptyState
        title="No runs yet"
        description="Click Run on the canvas to trigger this workflow — it'll show up here."
      />
    );
  }

  return (
    <div style={cardListStyle}>
      {list.items.map((r) => (
        <RunCard
          key={r.id}
          run={r}
          versionLabel={versionLabel(r.workflowVersionId)}
          onOpen={() => onOpenRun(r.id)}
        />
      ))}
      {list.isFetchingNextPage ? (
        <div style={loadingFooter}>
          <Spinner />
        </div>
      ) : null}
      {!list.hasMore && list.items.length > 0 ? (
        <div style={endOfListFooter}>
          End of list · {list.total} run{list.total === 1 ? '' : 's'}
        </div>
      ) : null}
      <InfiniteScrollSentinel
        enabled={list.hasMore && !list.isFetchingNextPage}
        onVisible={list.loadMore}
      />
    </div>
  );
}

const loadingFooter: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  padding: 'var(--sp-3) 0',
};

const endOfListFooter: CSSProperties = {
  textAlign: 'center',
  padding: 'var(--sp-3) 0',
  fontSize: 'var(--fs-xs)',
  color: 'var(--text-secondary)',
};

function RunCard({
  run,
  versionLabel,
  onOpen,
}: {
  run: Run;
  versionLabel: string;
  onOpen: () => void;
}) {
  return (
    <article
      style={cardStyle}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpen();
      }}
    >
      <div style={cardHeaderRow}>
        <code className="t-mono" style={{ fontSize: 'var(--fs-sm)' }}>
          {run.id.slice(0, 16)}
        </code>
        <StatusBadge status={run.status} />
      </div>
      <div style={cardMetaRow}>
        <span>
          <span className="t-mono-label">Trigger</span> {run.trigger}
        </span>
        <span>
          <span className="t-mono-label">Version</span> {versionLabel}
        </span>
        <span>
          <span className="t-mono-label">Started</span> {formatRelative(run.startedAt)}
        </span>
        <span>
          <span className="t-mono-label">Duration</span>{' '}
          {formatDuration(run.startedAt, run.finishedAt)}
        </span>
      </div>
      {run.error?.message ? (
        <p className="t-xs t-danger" style={{ margin: 0 }}>
          {String(run.error.message).slice(0, 240)}
        </p>
      ) : null}
      <div style={{ fontSize: 'var(--fs-xs)' }}>
        <Link
          to={routes.runDetail(run.id)}
          style={{ color: 'var(--text-secondary)' }}
          onClick={(e) => e.stopPropagation()}
        >
          Open full run page →
        </Link>
      </div>
    </article>
  );
}
